import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { useScrollOverflow } from '@hooks/useScrollOverflow';
import type { GatedFeature } from '@parlay-picker/shared/featureAccess';
import { Menu } from 'lucide-react';
import { type ReactElement, useRef, useState } from 'react';
import { ANALYTICS_TABS, type Tab, type TabId } from '../types/tabs';
import { AnalyticsDropdown } from './ui/AnalyticsDropdown';
import { GenerationIndicator } from './ui/GenerationIndicator';
import { TierBadge } from './ui/TierBadge';
import UpgradeModal from './ui/UpgradeModal';
import { UserMenu } from './ui/UserMenu';

interface AppHeaderProps {
  activeTab: TabId;
  setActiveTab: (tab: TabId) => void;
  visiblePrimaryTabs: Tab[];
  shareUnviewedCount: number;
  onOpenSettings: () => void;
  onOpenMobileNav: () => void;
  isAdmin: boolean;
}

export function AppHeader({
  activeTab,
  setActiveTab,
  visiblePrimaryTabs,
  shareUnviewedCount,
  onOpenSettings,
  onOpenMobileNav,
  isAdmin,
}: AppHeaderProps): ReactElement {
  const { isLocked } = useFeatureAccess();
  const [subNavUpgradeFeature, setSubNavUpgradeFeature] = useState<GatedFeature | null>(null);
  const analyticsSubNavRef = useRef<HTMLDivElement>(null);
  const { canScrollRight: analyticsSubNavOverflows } = useScrollOverflow(analyticsSubNavRef);

  return (
    <header className="sticky top-0 z-30 px-3 sm:px-6 border-b bg-theme-bg/80 backdrop-blur-xl py-2 border-theme-border/30 overflow-visible">
      <div className="flex items-center justify-between gap-2 sm:gap-3 max-w-7xl mx-auto">
        {/* Left: Logo + Navigation */}
        <div className="flex items-center gap-1 sm:gap-3 min-w-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-theme-primary-fill flex items-center justify-center shadow-lg shadow-theme-primary/20">
              <span className="text-theme-on-primary-fill font-bold text-sm">PP</span>
            </div>
            <span className="text-lg font-bold whitespace-nowrap hidden sm:block">
              Parlay Picker
            </span>
          </div>

          {/* Hamburger — visible on small screens OR when chat sidebar is open */}
          <button
            type="button"
            data-testid="mobile-nav-trigger"
            aria-label="Open navigation menu"
            onClick={onOpenMobileNav}
            className="@6xl:hidden p-2 rounded-lg hover:bg-theme-surface-hover transition-colors"
          >
            <Menu className="w-6 h-6 text-theme-text-secondary" />
          </button>

          {/* Desktop Navigation — hidden when chat sidebar is open */}
          <nav className="hidden @6xl:flex items-center gap-0.5 bg-theme-surface/50 rounded-xl p-1">
            {visiblePrimaryTabs.map((tab) => (
              <button
                type="button"
                key={tab.id}
                data-testid={`primary-nav-${tab.id}`}
                data-active={activeTab === tab.id ? 'true' : 'false'}
                onClick={() => setActiveTab(tab.id)}
                className={`relative px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-theme-primary-fill text-theme-on-primary-fill shadow-sm'
                    : 'text-theme-text-secondary hover:text-theme-text hover:bg-theme-surface-hover'
                }`}
              >
                {tab.shortLabel ? (
                  <>
                    <span className="@[90rem]:hidden">{tab.shortLabel}</span>
                    <span className="hidden @[90rem]:inline">{tab.label}</span>
                  </>
                ) : (
                  tab.label
                )}
                {tab.id === 'feed' && shareUnviewedCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-theme-danger text-[10px] text-white rounded-full flex items-center justify-center font-bold">
                    {shareUnviewedCount}
                  </span>
                )}
              </button>
            ))}
            <div className="w-px h-5 bg-theme-border/50 mx-0.5" />
            <AnalyticsDropdown activeTab={activeTab} setActiveTab={setActiveTab} />
          </nav>
        </div>

        {/* Divider between nav and actions */}
        <div className="hidden @6xl:block w-px h-6 bg-theme-border/40 shrink-0" />

        {/* Right: User Menu */}
        <UserMenu
          onOpenSettings={onOpenSettings}
          onNavigateAdmin={isAdmin ? () => setActiveTab('admin') : undefined}
        />
      </div>

      {/* Analytics sub-nav when an analytics tab is active */}
      {ANALYTICS_TABS.some((t) => t.id === activeTab) && (
        <div className="relative max-w-7xl mx-auto">
          {analyticsSubNavOverflows && (
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-theme-bg to-transparent z-10 pointer-events-none" />
          )}
          <div
            ref={analyticsSubNavRef}
            className="flex items-center gap-1 mt-2 overflow-x-auto pb-1 scrollbar-none"
          >
            {ANALYTICS_TABS.map((tab) => {
              const locked = tab.id !== 'summary' && isLocked('analytics_advanced');
              return (
                <button
                  type="button"
                  key={tab.id}
                  onClick={() => {
                    if (locked) {
                      setSubNavUpgradeFeature('analytics_advanced');
                      return;
                    }
                    setActiveTab(tab.id);
                  }}
                  className={`px-3 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${
                    activeTab === tab.id
                      ? 'bg-theme-primary/20 text-theme-primary border border-theme-primary/30'
                      : 'text-theme-text-tertiary hover:text-theme-text-secondary hover:bg-theme-surface-hover'
                  }`}
                >
                  {tab.label}
                  {locked && <TierBadge feature="analytics_advanced" />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {subNavUpgradeFeature && (
        <UpgradeModal
          mode="feature"
          feature={subNavUpgradeFeature}
          onDismiss={() => setSubNavUpgradeFeature(null)}
        />
      )}

      {/* Generation progress banner — inline in header to avoid overlapping controls */}
      <GenerationIndicator onLoadPicks={() => setActiveTab('picks')} />
    </header>
  );
}
